import { AUTO_FILTER_UNSUPPORT_PROVIDER, PROVIDER_CATEGORY_PREFIX } from 'root/util/constants';
import { gu_proceedNestedObjectWithVM } from 'root/util/general-util';

const normalizeKey = (value) =>
    String(value ?? '')
        .trim()
        .toLowerCase();

const getProviderKeys = (item) => [item?.provider, item?.providerCode, item?.providerName, item?.name].filter(Boolean).map(normalizeKey);

const getSubmenuSettings = (menuSlice, vm) => {
    const countryLanguageKey = vm?.props?.language?.countryLanguageKey;
    const viewType = vm?.props?.screen?.viewType;

    return menuSlice?.menuSettings?.[countryLanguageKey]?.[viewType]?.submenu || {};
};

const buildEnabledProviderIndex = (subCategoryMenus = {}, submenuSettings = {}) => {
    const providersByCategory = {};
    const allProviders = new Set();

    Object.entries(subCategoryMenus).forEach(([categoryKey, menuProviders]) => {
        const categoryProviders = new Set();

        (menuProviders || []).forEach((menuProvider) => {
            getProviderKeys(menuProvider).forEach((providerKey) => {
                categoryProviders.add(providerKey);
                allProviders.add(providerKey);
            });
        });

        providersByCategory[normalizeKey(categoryKey)] = categoryProviders;
    });

    const orderingsByCategory = {};
    Object.entries(submenuSettings).forEach(([categoryKey, setting]) => {
        const ordering = setting?.ordering;
        if (Array.isArray(ordering) && ordering.length) orderingsByCategory[normalizeKey(categoryKey)] = ordering.map(normalizeKey);
    });

    return { providersByCategory, allProviders, orderingsByCategory };
};

const getGameCategoryKey = (game) => {
    const rawCategory = game?.category ? String(game.category) : '';
    if (!rawCategory) return null;

    const aliasKey = PROVIDER_CATEGORY_PREFIX[rawCategory] || PROVIDER_CATEGORY_PREFIX[rawCategory.toUpperCase()];

    return normalizeKey(aliasKey || rawCategory);
};

const isProviderInOrdering = (gameProviderKeys, ordering, ordering2) =>
    ordering.some((name) => gameProviderKeys.includes(normalizeKey(name))) ||
    ordering2.some((providerObject) => gameProviderKeys.includes(normalizeKey(providerObject.name)) || gameProviderKeys.includes(normalizeKey(providerObject.provider)));

const getEnabledProviderScope = (game, { providersByCategory, allProviders, orderingsByCategory }, subCategoryMenus) => {
    const gameProviderKeys = getProviderKeys(game);
    const gameCategoryKey = getGameCategoryKey(game);

    if (!gameCategoryKey) return allProviders;

    const categoryScope = providersByCategory[gameCategoryKey];
    const ordering = orderingsByCategory[gameCategoryKey];
    const ordering2 = subCategoryMenus?.[gameCategoryKey] || subCategoryMenus?.[gameCategoryKey?.toUpperCase?.()];

    if (ordering || ordering2) {
        if (!isProviderInOrdering(gameProviderKeys, ordering, ordering2)) return null;

        return new Set([...(categoryScope || []), ...ordering]);
    }

    if (categoryScope) {
        if (gameProviderKeys.some((key) => categoryScope.has(key))) return categoryScope;

        return null;
    }

    const matchedCategoryKey = Object.keys(providersByCategory).find((categoryKey) => gameProviderKeys.some((key) => providersByCategory[categoryKey].has(key)));

    return (matchedCategoryKey && providersByCategory[matchedCategoryKey]) ?? allProviders;
};

const isInSupportedProviderList = (game, supportedProviderList) => {
    if (!supportedProviderList) return true;
    const supportedKeys = new Set([...supportedProviderList].map(normalizeKey));

    return getProviderKeys(game).some((key) => supportedKeys.has(key));
};

const isGameProviderEnabled = (game, enabledProviderScope) => !!enabledProviderScope && getProviderKeys(game).some((key) => enabledProviderScope.has(key));

const isFallbackTargetProviderEnabled = (game, enabledProviderScope) => {
    const targetProviders = [].concat(game.fallbackGameForProvider ?? []);

    return targetProviders.some((targetProvider) => enabledProviderScope.has(normalizeKey(targetProvider)));
};

export function isMenuSettingsReady(menuSlice) {
    const subCategoryMenus = menuSlice?.processedBaseMenuSettings?.subCategoryMenus;

    return Boolean(subCategoryMenus && Object.keys(subCategoryMenus).length);
}

export function filterHotGameListByMenuSettings(gameList = [], menuSlice, supportedProviderList, isFilterHotGames = true, vm) {
    if (!isFilterHotGames) return gameList;

    return filterGamesByMenuSettings(gameList, menuSlice, supportedProviderList, vm);
}

export function filterGamesByMenuSettings(gameList = [], menuSlice, supportedProviderList, vm) {
    const isAutoFilterUnsupportedProvider =
        gu_proceedNestedObjectWithVM(vm, window?.portalSettings?.features?.autoFilterUnsupportedProvider) ?? AUTO_FILTER_UNSUPPORT_PROVIDER;

    if (!isAutoFilterUnsupportedProvider) return gameList;

    const subCategoryMenus = menuSlice?.processedBaseMenuSettings?.subCategoryMenus || {};
    const submenuSettings = getSubmenuSettings(menuSlice, vm);
    const enabledProviderIndex = buildEnabledProviderIndex(subCategoryMenus, submenuSettings);

    if (enabledProviderIndex.allProviders.size === 0) return gameList;

    return (gameList || []).filter((game) => {
        const enabledProviderScope = getEnabledProviderScope(game, enabledProviderIndex, subCategoryMenus);
        if (!enabledProviderScope) return false;

        if (!isInSupportedProviderList(game, supportedProviderList)) return false;

        const isProviderEnabled = isGameProviderEnabled(game, enabledProviderScope);
        const isFallbackRedundant = isFallbackTargetProviderEnabled(game, enabledProviderScope);

        return isProviderEnabled && !isFallbackRedundant;
    });
}
